/**
 * Rate Mission sub-KPI type.
 *
 * A percentage or proportion vs a target rate. Bounded 0–100% (typically).
 *
 * Examples: graduation rate, vaccination coverage, retention rate,
 * customer satisfaction percentage, voter turnout.
 *
 * Distinguished from Improvement: Rate is bounded by its nature (a
 * percentage cannot exceed 100%), and the calculation is conceptually
 * simpler — current rate divided by target rate. Improvement allows
 * unbounded measures and tracks shift from a non-zero baseline.
 *
 * Scoring math:
 *   score = round(min(100, (current_rate / target_rate) × 100))
 *
 * Trend uses direction-aware delta with goal_direction='increase'.
 * Deadline degradation is configurable per-instance.
 *
 * See: DEV-814 KPI Engine Refinement, Phase 2.
 */

import type {
  ConfigFieldSchema,
  MissionDataPoint,
  MissionSubKpiType,
  SetupExample,
  SubKpiScore,
  UiHints,
  ValidationError, // DEV-963: surfaced after TS 5.4 upgrade.
} from './types';
import { latestAsOf } from '../timeSeries';
import { applyDeadlineDegradation } from './deadline';
import { KPI_DEFAULTS } from '../defaults';
import { validateCommonCrossFields } from './commonValidations';

/**
 * Configuration for a Rate sub-KPI instance.
 *
 * Data points carry the current rate as a number (percentage points,
 * e.g., 87 for 87%). The score compares current vs target rate.
 */
export interface RateConfig extends Record<string, unknown> {
  /** Display name for the metric (e.g., "Vaccination Coverage Rate"). */
  display_name: string;
  /** Brief description of how the rate is calculated. */
  calculation_description?: string;
  /** Target rate as percentage (0-100, may exceed 100 for stretch goals). */
  target_rate: number;
  /** Optional deadline (YYYY-MM-DD) for reaching the target rate. */
  deadline_date?: string;
  /** Days before deadline to trigger Yellow warning. Default 30. */
  warning_days?: number;
  /** Days before deadline to trigger Red critical. Default 7. */
  critical_days?: number;
  /** Whether deadline-proximity degradation is applied. Default true. */
  deadline_degradation_enabled?: boolean;
  /** Score >= this is green. Default 80. */
  green_threshold?: number;
  /** Score >= this (and < green_threshold) is yellow. Default 60. */
  yellow_threshold?: number;
}

/* ------------------------------------------------------------------------- */
/*  Scoring                                                                  */
/* ------------------------------------------------------------------------- */

function scoreRate(
  config: RateConfig,
  data: MissionDataPoint[],
  asOfDate: string,
): SubKpiScore {
  const { target_rate } = config;

  const pts = data.map((d) => ({ date: d.date, value: d.value }));
  const cur = latestAsOf(pts, asOfDate);

  if (!cur) {
    return {
      score_0_100: null,
      progress_0_100: null,
      current_value: null,
      target_value: target_rate,
      baseline_value: 0,
      explain: 'No rate data available as of this date.',
    };
  }

  const currentRate = cur.value;

  // Defensive: zero or negative target is meaningless.
  if (target_rate <= 0) {
    return {
      score_0_100: null,
      progress_0_100: null,
      current_value: currentRate,
      target_value: target_rate,
      baseline_value: 0,
      explain: 'Target rate must be positive — score not computed.',
    };
  }

  const ratio = currentRate / target_rate;
  const baseScore = Math.round(Math.max(0, Math.min(100, ratio * 100)));
  const goalMet = currentRate >= target_rate;

  const degraded = applyDeadlineDegradation({
    base_score: baseScore,
    goal_met: goalMet,
    asOfDate,
    config: {
      deadline_date: config.deadline_date,
      warning_days: config.warning_days,
      critical_days: config.critical_days,
      deadline_degradation_enabled: config.deadline_degradation_enabled,
    },
  });

  const baseExplain = goalMet
    ? `Target rate met: ${currentRate}% (target ${target_rate}%).`
    : `Current rate ${currentRate}% of target ${target_rate}% (${baseScore}% of target).`;

  return {
    score_0_100: degraded.score,
    // DEV-864: Raw progress = (current/target) * 100 with NO deadline penalty.
    // Drives the progress-bar fill so it tracks user input proportionally.
    progress_0_100: baseScore,
    current_value: currentRate,
    target_value: target_rate,
    baseline_value: 0,
    explain: degraded.explain ? `${degraded.explain} ${baseExplain}` : baseExplain,
  };
}

/* ------------------------------------------------------------------------- */
/*  Color derivation                                                         */
/* ------------------------------------------------------------------------- */

function colorFromScore(
  score: SubKpiScore,
  config: RateConfig,
): 0 | 1 | 2 | 3 {
  if (score.score_0_100 === null) return 0;
  const greenAt = config.green_threshold ?? KPI_DEFAULTS.mission.bands.green_threshold;
  const yellowAt = config.yellow_threshold ?? KPI_DEFAULTS.mission.bands.yellow_threshold;
  if (score.score_0_100 >= greenAt) return 3;
  if (score.score_0_100 >= yellowAt) return 2;
  return 1;
}

/* ------------------------------------------------------------------------- */
/*  Validation                                                               */
/* ------------------------------------------------------------------------- */

function validateConfig(config: RateConfig): ValidationError | null {
  // DEV-816 / Skip: per-field required checks live in the schema. Only
  // non-trivial cross-field / range rules belong here.
  if (Number.isFinite(config.target_rate) && config.target_rate <= 0) {
    return { field: 'target_rate', message: 'Target rate must be positive.' };
  }
  return validateCommonCrossFields(config);
}

/* ------------------------------------------------------------------------- */
/*  Schema, examples, and UI hints                                           */
/* ------------------------------------------------------------------------- */

const config_schema: ConfigFieldSchema[] = [
  {
    key: 'display_name',
    label: 'What rate are you measuring?',
    type: 'string',
    required: true,
    help_text:
      'A short name for the rate (e.g., "Vaccination Coverage", "Graduation Rate").',
  },
  {
    key: 'calculation_description',
    label: 'How is the rate calculated?',
    type: 'string',
    required: false,
    help_text:
      'Brief description so others understand what the percentage means ' +
      '(e.g., "Vaccinated members / total target population").',
  },
  {
    key: 'target_rate',
    label: 'Target rate (%)',
    type: 'number',
    required: true,
    help_text: 'The target percentage to reach. Must be positive.',
    validation: { min: 0, max: 100 },
  },
  {
    key: 'tracking_start_date',
    label: 'Tracking start date (optional)',
    type: 'date',
    required: false,
    help_text: 'Scoring begins on this date. Reporting periods before this date are ignored.',
    inline_with_next: true, // pair with deadline_date (DEV-816)
  },
  {
    key: 'deadline_date',
    label: 'Target deadline (optional)',
    type: 'date',
    required: false,
    help_text: 'When the target rate should be reached.',
  },
  // deadline_degradation_enabled removed (DEV-814 / Skip): redundant once
  // color_mode is explicit. See count.ts for the rationale.
  // warning_days / critical_days deliberately NOT exposed in the form (DEV-814).
  // They were a redundant second pair of red/yellow controls. Deadline
  // degradation still uses internal defaults (KPI_DEFAULTS.deadline) when
  // deadline_degradation_enabled is true. The user only configures the SCORE
  // thresholds below; deadline pressure caps the score into the right band.
  {
    key: 'color_mode',
    label: 'Color the indicator from',
    type: 'enum',
    required: false,
    default_value: 'score',
    enum_options: [
      {
        value: 'score',
        label: 'Rate (% of target rate)',
        help: 'Color is driven by current rate as a percentage of the target rate. Set the % thresholds below for green/yellow/red.',
      },
      {
        value: 'deadline',
        label: 'Time remaining until deadline',
        help: 'Color is driven by days remaining: green far from deadline, yellow within the warning window, red within the critical window. Useful when the deadline matters more than the rate.',
      },
    ],
    help_text: 'Pick how the indicator color is computed.',
  },
  // Labels reframed for consistency (DEV-814 / Skip): see count.ts.
  {
    key: 'green_threshold',
    label: 'Yellow when % of target rate drops below',
    type: 'number',
    required: false,
    default_value: 80,
    suffix: '%',
    help_text: 'Indicator turns yellow once the rate as a % of target rate drops below this number. Default 80.',
    validation: { min: 0, max: 100 },
    visible_when: (c) => (c.color_mode ?? 'score') === 'score',
    inline_with_next: true, // pair with red threshold (DEV-816)
  },
  {
    key: 'yellow_threshold',
    label: 'Red when % of target rate drops below',
    type: 'number',
    required: false,
    default_value: 60,
    suffix: '%',
    help_text: 'Indicator turns red once the rate as a % of target rate drops below this number. Must be less than yellow. Default 60.',
    validation: { min: 0, max: 100 },
    visible_when: (c) => (c.color_mode ?? 'score') === 'score',
  },
  // Deadline-mode windows reframed (DEV-816 / Skip) to "drops below" pattern.
  {
    key: 'warning_days',
    label: 'Yellow when days until deadline drops below',
    type: 'number',
    required: false,
    default_value: 30,
    suffix: 'days',
    help_text: 'Indicator turns yellow once fewer than this many days remain until the deadline. Default 30.',
    validation: { min: 0 },
    visible_when: (c) => c.color_mode === 'deadline',
    inline_with_next: true, // pair with critical_days (DEV-816)
  },
  {
    key: 'critical_days',
    label: 'Red when days until deadline drops below',
    type: 'number',
    required: false,
    default_value: 7,
    suffix: 'days',
    help_text: 'Indicator turns red once fewer than this many days remain until the deadline. Must be less than yellow. Default 7.',
    validation: { min: 0 },
    visible_when: (c) => c.color_mode === 'deadline',
  },
];

const setup_examples: SetupExample[] = [
  {
    title: 'Vaccination coverage rate',
    description:
      'Track vaccination coverage as a percentage of the target population, ' +
      'with a target rate of 95%.',
    example_config: {
      display_name: 'Vaccination Coverage Rate',
      calculation_description: 'Vaccinated members / total target population',
      target_rate: 95,
      deadline_date: '2026-12-31',
    },
  },
  {
    title: 'Graduation rate',
    description:
      'Track student graduation rate, with a target of 90% of enrolled cohort completing the program.',
    example_config: {
      display_name: 'Graduation Rate',
      calculation_description: 'Students completed / students enrolled',
      target_rate: 90,
    },
  },
  {
    title: 'Voter turnout rate',
    description:
      'Track voter turnout for a target election as a percentage of registered voters, ' +
      'target 75%.',
    example_config: {
      display_name: 'Voter Turnout',
      calculation_description: 'Votes cast / registered voters in target area',
      target_rate: 75,
      deadline_date: '2026-11-03',
    },
  },
];

const ui_hints: UiHints = {
  default_chart: 'gauge',
  value_format: 'percentage',
  show_target_line: true,
};

/* ------------------------------------------------------------------------- */
/*  Export                                                                   */
/* ------------------------------------------------------------------------- */

export const rateType: MissionSubKpiType<RateConfig> = {
  type_key: 'rate',
  display_name: 'Rate',
  description:
    'Track a percentage measurement against a target percentage. No baseline — just current vs goal. For baseline-based tracking (e.g., 60% → 80%), use Improvement with "%" as the unit.',
  examples: [
    'vaccination coverage (% of population)',
    'graduation rate (% of cohort)',
    'customer satisfaction (% satisfied)',
  ],
  use_when: 'What percent of the group has reached the goal?',
  supported_color_modes: ['score', 'deadline'],
  goal_direction: 'increase',
  config_schema,
  validate_config: validateConfig,
  score: scoreRate,
  color_from_score: colorFromScore,
  ui_hints,
  setup_examples,
};
